import React from 'react';
import { motion } from 'framer-motion';
import { CarIcon, PaletteIcon, LampDeskIcon, LayoutIcon, GemIcon, CheckCircle2Icon, LightbulbIcon, SlidersHorizontalIcon, LayersIcon, ArrowRightIcon } from 'lucide-react';
export function NoirAtelierSection() {
  return <section id="noir-atelier" className="relative w-full bg-dark-900 overflow-hidden" data-id="element-133">
      {/* HEADER & HERO */}
      <div className="relative pt-24 md:pt-36 pb-20 px-6 lg:px-8" data-id="element-134">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-gold/[0.03] rounded-full blur-[120px] pointer-events-none" data-id="element-135" />

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-136">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8
        }} className="text-center mb-12" data-id="element-137">
            <p className="text-gold/60 text-xs font-semibold tracking-[0.5em] uppercase mb-6 font-body" data-id="element-138">
              Flagship Design Conversion
            </p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4" data-id="element-139">
              KIA PV5 <span className="text-gold" data-id="element-140">NOIR</span> ATELIER
            </h2>
            <p className="font-display text-2xl sm:text-3xl text-white/80 italic mb-6" data-id="element-141">
              Designed to Live Beautifully.
            </p>
            <p className="text-white/50 text-base max-w-2xl mx-auto font-body leading-relaxed" data-id="element-142">
              A minimalist luxury campervan conversion inspired by architectural
              interiors, boutique hotels, and modern automotive design language.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative rounded-2xl overflow-hidden mb-16 aspect-[21/9] border border-white/5 shadow-2xl" data-id="element-143">
            <img src="https://cdn.magicpatterns.com/uploads/sZVjrUyD2zp5HNuMCu6CBm/WhatsApp_Image_2026-04-03_at_12.38.52_AM.jpg" alt="Kia PV5 Noir Atelier with pop-top roof raised, rear three-quarter view in workshop" className="w-full h-full object-cover" data-id="element-144" />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/20 to-transparent" data-id="element-145" />
            <div className="absolute bottom-8 left-8 md:bottom-12 md:left-12" data-id="element-146">
              <p className="text-gold text-xs tracking-[0.4em] uppercase font-body mb-2" data-id="element-147">
                Vanscape
              </p>
              <p className="text-white/80 text-sm font-body tracking-widest uppercase" data-id="element-148">
                Showroom Spec Sheet
              </p>
            </div>
          </motion.div>

          {/* VEHICLE OVERVIEW */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-149">
            {[{
            label: 'Base Vehicle',
            value: 'Kia PV5 Cargo'
          }, {
            label: 'Conversion Type',
            value: 'Luxury Campervan (Flagship Atelier Edition)'
          }, {
            label: 'Exterior Theme',
            value: 'White body with black accent detailing'
          }, {
            label: 'Target Positioning',
            value: 'Premium lifestyle / design-led mobility'
          }].map((item, i) => <div key={i} className="bg-dark-800/50 border border-white/5 rounded-xl p-6" data-id="element-150">
                <p className="text-gold/60 text-xs uppercase tracking-widest mb-2 font-body" data-id="element-151">
                  {item.label}
                </p>
                <p className="text-white/90 text-sm font-medium font-body" data-id="element-152">
                  {item.value}
                </p>
              </div>)}
          </motion.div>
        </div>
      </div>

      {/* POSITIONING & CONCEPT */}
      <div className="bg-dark-800 py-24 px-6 lg:px-8 border-y border-white/5" data-id="element-153">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center" data-id="element-154">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7
        }} data-id="element-155">
            <h3 className="font-display text-3xl md:text-4xl font-bold mb-6" data-id="element-156">
              The <span className="text-gold italic" data-id="element-157">Quiet Luxury</span> Space
            </h3>
            <p className="text-white/60 text-lg leading-relaxed font-body mb-8" data-id="element-158">
              The Kia PV5 Noir Atelier redefines the campervan as a premium
              living space. It removes the visual language of traditional
              motorhomes and replaces it with the calm precision of modern
              architectural interiors. Combining Japandi calm aesthetics with
              automotive precision.
            </p>
            <div className="p-6 border-l-2 border-gold bg-dark-900/30" data-id="element-159">
              <p className="text-white/80 text-xl font-display italic" data-id="element-160">
                "This is not a travel vehicle. It is a mobile design space."
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="grid gap-4" data-id="element-161">
            {['Minimal visual noise', 'Warm, layered lighting', 'Natural materials with tactile depth', 'Integrated technology, fully concealed', 'Open, breathable spatial flow'].map((principle, i) => <div key={i} className="flex items-center gap-4 bg-dark-900/40 p-4 rounded-lg border border-white/5" data-id="element-162">
                <CheckCircle2Icon className="text-gold flex-shrink-0" size={20} data-id="element-163" />
                <span className="text-white/80 font-body" data-id="element-164">{principle}</span>
              </div>)}
          </motion.div>
        </div>
      </div>

      {/* PALETTE & MATERIALS */}
      <div className="py-24 px-6 lg:px-8" data-id="element-165">
        <div className="max-w-7xl mx-auto" data-id="element-166">
          <div className="grid lg:grid-cols-2 gap-16" data-id="element-167">
            {/* Colour Palette */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.7
          }} data-id="element-168">
              <div className="flex items-center gap-3 mb-8" data-id="element-169">
                <PaletteIcon className="text-gold" size={24} data-id="element-170" />
                <h3 className="font-display text-2xl font-bold" data-id="element-171">
                  Interior Colour Palette
                </h3>
              </div>
              <div className="space-y-6" data-id="element-172">
                {[{
                label: 'Primary',
                value: 'Warm off-white / stone ivory',
                color: '#F5F5F0'
              }, {
                label: 'Wood',
                value: 'Light oak / ash veneer (matte finish)',
                color: '#D4B895'
              }, {
                label: 'Accent',
                value: 'Matte black detailing',
                color: '#1A1A1A'
              }, {
                label: 'Soft Furnishings',
                value: 'Taupe, beige, sand tones',
                color: '#B8A99A'
              }, {
                label: 'Surface Finish',
                value: 'Anti-glare matte textures throughout',
                color: 'transparent',
                border: true
              }].map((item, i) => <div key={i} className="flex items-center gap-6" data-id="element-173">
                    <div className={`w-12 h-12 rounded-full flex-shrink-0 shadow-inner ${item.border ? 'border border-white/20' : ''}`} style={{
                  backgroundColor: item.color
                }} data-id="element-174" />
                    <div data-id="element-175">
                      <p className="text-gold text-xs uppercase tracking-widest font-body mb-1" data-id="element-176">
                        {item.label}
                      </p>
                      <p className="text-white/80 text-sm font-body" data-id="element-177">
                        {item.value}
                      </p>
                    </div>
                  </div>)}
              </div>
            </motion.div>

            {/* Material Spec */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-178">
              <div className="flex items-center gap-3 mb-8" data-id="element-179">
                <LayersIcon className="text-gold" size={24} data-id="element-180" />
                <h3 className="font-display text-2xl font-bold" data-id="element-181">
                  Material Specification
                </h3>
              </div>
              <div className="grid gap-4" data-id="element-182">
                {['Real wood veneer cabinetry (flat slab design)', 'Soft-touch matte composite panels', 'Brushed black metal detailing (hardware & trims)', 'Solid surface countertop (light terrazzo or warm white)', 'Automotive-grade upholstery (leather or woven textile)', 'Fabric wall panel inserts (select zones)'].map((material, i) => <div key={i} className="flex items-start gap-4 p-5 bg-dark-800/50 rounded-xl border border-white/5" data-id="element-183">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" data-id="element-184" />
                    <p className="text-white/70 text-sm font-body leading-relaxed" data-id="element-185">
                      {material}
                    </p>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* LIGHTING & FEATURES */}
      <div className="bg-dark-800 py-24 px-6 lg:px-8 border-y border-white/5" data-id="element-186">
        <div className="max-w-7xl mx-auto" data-id="element-187">
          <div className="grid lg:grid-cols-2 gap-16" data-id="element-188">
            {/* Lighting */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.7
          }} data-id="element-189">
              <div className="flex items-center gap-3 mb-8" data-id="element-190">
                <LightbulbIcon className="text-gold" size={24} data-id="element-191" />
                <h3 className="font-display text-2xl font-bold" data-id="element-192">
                  Lighting Design System
                </h3>
              </div>
              <div className="space-y-4" data-id="element-193">
                {[{
                title: 'Layered Ambient (2700K)',
                desc: 'Under-cabinet LED strip lighting, bed base and kickboard glow lighting, concealed ceiling perimeter lighting.'
              }, {
                title: 'Feature Lighting',
                desc: 'Linear recessed ceiling light channel OR fibre-optic starlight ceiling.'
              }, {
                title: 'Task Lighting',
                desc: 'Aircraft-style directional reading lights, under-cabinet kitchen illumination.'
              }, {
                title: 'Control',
                desc: 'Multi-zone dimmable system (touch or app-controlled).'
              }].map((light, i) => <div key={i} className="p-6 bg-dark-900/40 rounded-xl border border-white/5" data-id="element-194">
                    <h4 className="text-gold text-sm font-semibold tracking-wide mb-2 font-body" data-id="element-195">
                      {light.title}
                    </h4>
                    <p className="text-white/60 text-sm font-body leading-relaxed" data-id="element-196">
                      {light.desc}
                    </p>
                  </div>)}
              </div>
            </motion.div>

            {/* Signature Features */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-197">
              <div className="flex items-center gap-3 mb-8" data-id="element-198">
                <GemIcon className="text-gold" size={24} data-id="element-199" />
                <h3 className="font-display text-2xl font-bold" data-id="element-200">
                  Signature Design Features
                </h3>
              </div>
              <div className="grid gap-4" data-id="element-201">
                {['Handleless cabinetry with push-to-open systems', 'Hidden appliance integration', 'Floating furniture effect (concealed base lighting)', 'Seamless surface transitions', 'Minimal visible fixings or hardware'].map((feature, i) => <div key={i} className="flex items-center gap-4 p-5 bg-dark-900/40 rounded-xl border border-white/5 hover:border-gold/20 transition-colors" data-id="element-202">
                    <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0" data-id="element-203">
                      <span className="text-gold text-xs font-bold" data-id="element-204">
                        {i + 1}
                      </span>
                    </div>
                    <p className="text-white/80 text-sm font-body" data-id="element-205">{feature}</p>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* LAYOUT CONFIGURATION */}
      <div className="py-24 px-6 lg:px-8" data-id="element-206">
        <div className="max-w-7xl mx-auto" data-id="element-207">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7
        }} className="text-center mb-16" data-id="element-208">
            <LayoutIcon className="text-gold mx-auto mb-4" size={32} data-id="element-209" />
            <h3 className="font-display text-3xl md:text-4xl font-bold mb-4" data-id="element-210">
              Layout Configuration
            </h3>
            <p className="text-white/50 text-sm font-body uppercase tracking-widest" data-id="element-211">
              Design focus: uninterrupted central walkway for spatial openness
            </p>
          </motion.div>

          <div className="flex flex-col md:flex-row gap-4 relative" data-id="element-212">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-white/10 -translate-y-1/2 z-0" data-id="element-213" />

            {[{
            zone: 'Front Zone',
            desc: 'Swivel driver + passenger seats (lounge conversion)'
          }, {
            zone: 'Central Zone',
            desc: 'Slim side kitchen module (driver side) & opposing U-bench seating with integrated storage'
          }, {
            zone: 'Rear Zone',
            desc: 'Fixed convertible U-bed luxury sleeping system with hidden storage compartments beneath'
          }].map((zone, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.2
          }} className="flex-1 relative z-10" data-id="element-214">
                <div className="bg-dark-800 border border-white/10 rounded-2xl p-8 h-full flex flex-col items-center text-center hover:border-gold/30 transition-colors" data-id="element-215">
                  <div className="w-12 h-12 rounded-full bg-dark-900 border border-gold/30 flex items-center justify-center mb-6" data-id="element-216">
                    <span className="text-gold font-display font-bold" data-id="element-217">
                      {i + 1}
                    </span>
                  </div>
                  <h4 className="text-white font-display text-xl font-bold mb-4" data-id="element-218">
                    {zone.zone}
                  </h4>
                  <p className="text-white/60 text-sm font-body leading-relaxed" data-id="element-219">
                    {zone.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* BRAND IDENTITY & CTA */}
      <div className="bg-dark-800 py-24 px-6 lg:px-8 border-t border-white/5" data-id="element-220">
        <div className="max-w-4xl mx-auto" data-id="element-221">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="bg-dark-900 border border-gold/20 rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-222">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gold to-transparent opacity-50" data-id="element-223" />

            <h3 className="font-display text-2xl text-gold mb-8 tracking-widest uppercase" data-id="element-224">
              Brand Identity
            </h3>

            <div className="grid sm:grid-cols-2 gap-8 text-left mb-12" data-id="element-225">
              <div data-id="element-226">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-1 font-body" data-id="element-227">
                  Model Name
                </p>
                <p className="text-white font-display text-lg" data-id="element-228">
                  PV5 Noir Atelier
                </p>
              </div>
              <div data-id="element-229">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-1 font-body" data-id="element-230">
                  Brand Division
                </p>
                <p className="text-white font-display text-lg" data-id="element-231">Vanscape</p>
              </div>
              <div data-id="element-232">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-1 font-body" data-id="element-233">
                  Design Language
                </p>
                <p className="text-white font-display text-lg" data-id="element-234">
                  Architectural | Minimal | Premium | Calm
                </p>
              </div>
              <div data-id="element-235">
                <p className="text-white/40 text-xs uppercase tracking-widest mb-1 font-body" data-id="element-236">
                  Signature Element
                </p>
                <p className="text-white font-display text-lg" data-id="element-237">
                  Continuous ambient lighting line through the cabin interior
                </p>
              </div>
            </div>

            <div className="pt-8 border-t border-white/10" data-id="element-238">
              <p className="text-white/80 text-lg font-body mb-8" data-id="element-239">
                For enquiries, specifications, or bespoke builds, speak to the
                showroom team.
              </p>
              <a href="#contact" className="inline-flex items-center gap-3 px-8 py-4 bg-gold text-dark-900 font-semibold text-sm tracking-widest uppercase rounded hover:bg-gold-hover transition-all duration-300" data-id="element-240">
                Contact Showroom <ArrowRightIcon size={18} data-id="element-241" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}